import { inject, Injectable } from '@angular/core'

import * as Sentry from '@sentry/angular'
import { ReplaySubject } from 'rxjs'

import { AppConfigId, MyProfile } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { AuthSession, Profile } from '@ti/core/auth/services'
import { LOGIN_AS_SESSION } from '@ti/core/common/contracts'
import { MaintenanceEnforcer } from '@ti/core/common/services'
import { BrowserLocalStorage, BrowserSessionStorage } from '@ti/core/common/storage'
import { injectDestroy$ } from '@ti/core/common/utils'

import environment from '../env'
import { McpAuthContext } from './mcp/bootstrap/mcp-auth-context'
import { isMcpAuthDocument } from './mcp/bootstrap/mcp-interaction-capture'
import { mcpAttemptStore } from './mcp/state/mcp-attempt.store'

@Injectable({
  providedIn: 'root',
})
export class AppInitializer {
  protected readonly api = inject(ApiService)
  protected readonly maintenance = inject(MaintenanceEnforcer)
  protected readonly profile = inject(Profile)
  protected readonly authSession = inject(AuthSession)
  protected readonly loginAsSession = inject(LOGIN_AS_SESSION)
  protected readonly sessionStorage = inject(BrowserSessionStorage)
  protected readonly localStorage = inject(BrowserLocalStorage)
  protected readonly destroy$ = injectDestroy$()
  protected readonly mcpContext = inject(McpAuthContext)

  protected readonly data$ = new ReplaySubject<MyProfile>(1)

  public init() {
    // Maintenance stays on for the MCP documents too: the sign-in route they pass through is
    // behind its guard, which waits for this state.
    this.maintenance.init(AppConfigId.TSPAdmin)
    this.authSession.state.currentUserAttributes$.subscribe((user) => {
      // The person's email and name stay out of telemetry on the MCP authorization documents.
      if (environment.sentry.enabled && user && !isMcpAuthDocument) {
        Sentry.setUser({
          id: user.sub,
          username: user.name,
          email: user.email,
        })
      }
    })
    this.authSession.state.onSignedOut$.subscribe(() => {
      if (environment.sentry.enabled) {
        Sentry.setUser(null)
      }
      // The in-memory fallback is not covered by clearing sessionStorage.
      mcpAttemptStore.clearAll()
      this.sessionStorage.clear()
      this.localStorage.clear()
    })
    if (!this.loginAsSession) {
      this.profile.state.subscribe((profile) => {
        // Connecting an app must not write to the person's profile.
        if (profile && !this.mcpContext.isAuthFlow) {
          return this.updateTimezone(profile)
        }
      })
    }
  }

  protected async updateTimezone(profile: MyProfile) {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (browserTimezone && browserTimezone !== profile.timezone) {
      await this.api.tsp.user.setTimezone(browserTimezone)
    }
  }
}
